<template>
  <!-- Lazy loaded -->
  <div
    v-if="!doNotLazyLoad || placeholderRatio"
    :class="{
      'sg-img': true,
      'show-loading-img': loadingImg,
      'show-placeholder-img': placeholderRatio,
      loaded: loaded && complete,
    }"
  >
    <!-- Loading Image -->
    <img
      v-if="loadingImg"
      class="loading-img"
      :src="loadingImg"
      :alt="alt"
      :width="width"
      :height="height"
      :class="[objectFit !== 'contain' ? 'loading-img--' + objectFit : '']"
    />

    <!-- Placeholder image -->
    <PlaceholderImg
      v-else-if="placeholderRatio"
      class="placeholder-img"
      :ratio="placeholderRatio"
      :object-fit="objectFit"
    />

    <!-- Target image, lazy loaded -->
    <img
      v-if="!doNotLazyLoad"
      ref="img"
      :src="resolve(src)"
      loading="lazy"
      :alt="alt"
      :data-srcset="srcset"
      :width="width"
      :height="height"
      :class="[objectFit !== 'contain' ? 'loading-img--' + objectFit : '']"
      @load="loadListener"
    />

    <!-- Target image, not lazy loaded -->
    <img
      v-else
      ref="img"
      :src="resolve(src)"
      :alt="alt"
      :srcset="srcset"
      :width="width"
      :height="height"
      :class="[objectFit !== 'contain' ? 'loading-img--' + objectFit : '']"
      @load="loadListener"
    />
  </div>

  <!-- Not lazy loaded and no placeholder image-->
  <img
    v-else
    ref="img"
    :src="resolve(src)"
    :alt="alt"
    :class="[objectFit !== 'contain' ? 'loading-img--' + objectFit : '']"
    :width="width"
    :height="height"
    v-bind="$attrs"
    @load="loadListener"
  />
</template>

<script>
export default {
  name: 'SgImg',

  props: {
    src: { type: String, required: true },
    srcset: { type: String, default: '' },
    loadingImg: { type: String, default: '' },
    alt: { type: String, default: '' },
    doNotLazyLoad: { type: Boolean, default: false },
    placeholderRatio: { type: String, default: '' },
    width: { type: Number, default: 400 },
    height: { type: Number, default: 400 },
    objectFit: {
      type: String,
      default: 'contain',
      validator(value) {
        return ['contain', 'cover'].includes(value)
      },
    },
  },

  emits: ['load', 'change'],

  data() {
    return {
      loaded: false,
      complete: false,
    }
  },

  watch: {
    src() {
      this.loaded = false
      this.complete = false
      this.$emit('change')

      this.$nextTick(() => {
        this.checkComplete()
      })
    },
  },

  mounted() {
    this.checkComplete()
  },

  methods: {
    lazyListener(event) {
      if (event.src === this.resolvedSrc) {
        this.$emit('load')
        this.loaded = true
      }
    },
    loadListener() {
      this.checkComplete()
      this.$emit('load')
    },
    checkComplete() {
      this.complete = (this.$refs.img || {}).complete || false
    },
    resolve(src) {
      if (!src) return ''
      if (
        src.includes('/dimg') ||
        src.startsWith('http') ||
        src.startsWith('//')
      )
        return src
      
      return `~/assets/images${src.startsWith('/') ? '' : '/'}${src}`
      
      // return `${this.$config.staticAssetURL}/images${
      //   src.startsWith('/') ? '' : '/'
      // }${src}`
    },
  },
}
</script>

<style lang="postcss" scoped>
.show-loading-img,
.show-placeholder-img {
  position: relative;
  overflow: hidden;

  .loading-img {
    filter: blur(30px);
    transform: scale(1.2);
    object-fit: contain;
    transition: opacity 0.5s ease-in-out;
    opacity: 1;
    width: 100%;
  }

  .placeholder-img {
    width: 100%;
    opacity: 0;
  }

  &.loaded {
    .loading-img {
      opacity: 0;
    }

    :not(.loading-img) {
      opacity: 1;
    }
  }

  :not(.loading-img):not(.placeholder-img) {
    top: 50%;
    left: 0;
    width: 100%;
    transition: opacity 0.5s ease-in-out;
    transform: translateY(-50%);
    position: absolute;
  }

  &.show-placeholder-img {
    :not(.loading-img):not(.placeholder-img) {
      top: 0;
      transform: unset;
    }
  }

  &.show-loading-img:not(.loaded) :not(.loading-img) {
    opacity: 0;
  }
}

.loading-img--cover {
  object-fit: cover;
}

img[src=''],
img:not([src]) {
  content: url('data:image/gif;base64,R0lGODlhAQABAPAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==');
}
</style>
